import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";

interface VersionInfo {
  version: string;
  buildTime: string;
}

const CHECK_INTERVAL = 60 * 1000;
const REARM_DELAY = 5 * 60 * 1000;

export function useVersionCheck() {
  const initialVersion = useRef<VersionInfo | null>(null);
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const toastShown = useRef(false);

  useEffect(() => {
    const checkVersion = async () => {
      try {
        const response = await fetch(`/version.json?t=${Date.now()}`);
        if (!response.ok) return;

        const currentVersion: VersionInfo = await response.json();

        if (!initialVersion.current) {
          initialVersion.current = currentVersion;
          return;
        }

        const hasUpdate =
          currentVersion.version !== initialVersion.current.version ||
          currentVersion.buildTime !== initialVersion.current.buildTime;

        if (hasUpdate && !toastShown.current) {
          setUpdateAvailable(true);
          toastShown.current = true;

          toast("New version available", {
            description: "A new version of the app is available. Reload to update.",
            duration: Infinity,
            action: {
              label: "Reload",
              onClick: () => window.location.reload(),
            },
            onDismiss: () => {
              setTimeout(() => {
                toastShown.current = false;
              }, REARM_DELAY);
            },
          });
        }
      } catch (error) {
        console.debug("Version check failed:", error);
      }
    };

    const initialTimeout = setTimeout(checkVersion, 5000);
    const interval = setInterval(checkVersion, CHECK_INTERVAL);

    return () => {
      clearTimeout(initialTimeout);
      clearInterval(interval);
    };
  }, []);

  return { updateAvailable };
}
